import type { Task } from '@redux-saga/types'
import { normalize } from 'normalizr'
import * as R from 'ramda'
import {
  all,
  call,
  cancel,
  fork,
  put,
  take,
  takeLatest,
} from 'redux-saga/effects'
import {
  type ApiError,
  Defaults,
  type IdObject,
  LanguageUtils,
  type User,
  type UserWithFullPatients,
  Utils,
} from '@pbt/pbt-ui-components'

import * as API from '~/api'
import type { InventoryOrder } from '~/api/graphql/generated/types'
import * as Schema from '~/api/schemas'
import SearchContext from '~/constants/searchContext'
import SecretSearchContext from '~/constants/secretSearchContext'
import {
  type BaseSearchRequestParams,
  type SearchClientsAndPatientsRequestParams,
  type SearchContactsRequestParams,
  type SearchPersonsRequestParams,
  type SuggestionResult,
  TeamFilter,
} from '~/types'
import { getPatientStatus } from '~/utils'

import {
  type fetchMoreSuggestionResults,
  fetchMoreSuggestionResultsFailure,
  fetchMoreSuggestionResultsSuccess,
  type fetchSuggestionResults,
  fetchSuggestionResultsFailure,
  fetchSuggestionResultsSuccess,
} from '../actions/search'
import {
  CLEAR_SUGGESTION_RESULTS,
  FETCH_MORE_SUGGESTION_RESULTS,
  FETCH_SUGGESTION_RESULTS,
  FETCH_SUGGESTION_RESULTS_SUCCESS,
} from '../actions/types/search'
import requestAPI, { requestAPIForBusiness } from './utils/requestAPI'
import updateEntities from './utils/updateEntities'

const searchResultEntityMap = ({ id, name }: IdObject) => ({ id, item: name })

const SearchResultToListItemMap = {
  [SearchContext.INVENTORY]: searchResultEntityMap,
  [SearchContext.LAB_TESTS]: searchResultEntityMap,
  [SearchContext.PROCEDURES]: (procedure: IdObject) => ({
    id: procedure?.id,
    item: LanguageUtils.getTranslatedFieldName(procedure),
  }),
  [SearchContext.CLIENT_PATIENTS]: (client: UserWithFullPatients) => ({
    id: client.id,
    itemContextIds: client.clientInContextBusinesses,
    item: Utils.getPersonString(client),
    itemActive: client.active,
    subItems: (client.patients || []).map((patient) => {
      const { disabled, reasons } = getPatientStatus(patient)

      return {
        id: patient.id,
        name: patient.name,
        alert: disabled ? R.head(reasons) : undefined,
      }
    }),
    noSubItemsText: 'No patients',
  }),
  [SearchContext.REMINDER_PROTOCOLS]: searchResultEntityMap,
  [SecretSearchContext.PERSONS]: (person: User) => ({
    id: person.id,
    item: Utils.getPersonString(person),
  }),
  [SearchContext.CONTACTS]: searchResultEntityMap,
  [SearchContext.TASK_DASHBOARD]: searchResultEntityMap,
  [SearchContext.MARKETPLACE]: searchResultEntityMap,
  [SearchContext.IMAGING_DASHBOARD]: searchResultEntityMap,
  [SearchContext.PRACTICES]: searchResultEntityMap,
  [SearchContext.ON_HAND_CATALOG]: searchResultEntityMap,
  [SearchContext.INVENTORY_ORDERS]: (order: InventoryOrder) => ({
    id: order.id,
    item: order.orderNumber,
  }),
}

const formatSuggestions = (
  suggestions: SuggestionResult[],
): SuggestionResult[] => {
  const withSubItems = suggestions.map((suggestion) => {
    const subItems = suggestion.subItems || []
    const firstSubItem = R.head(subItems)

    const mainSuggestion = firstSubItem
      ? {
          ...suggestion,
          subItem: firstSubItem.name,
          subItemId: firstSubItem.id,
          subItemAlert: firstSubItem.alert,
        }
      : suggestion

    return [
      mainSuggestion,
      ...subItems.slice(1).map((subItem) => ({
        id: suggestion.id,
        subItem: subItem.name,
        subItemId: subItem.id,
        subItemAlert: subItem.alert,
        noSubItemsText: suggestion.noSubItemsText,
      })),
    ]
  })

  return R.flatten(withSubItems)
}

const getSearchContextApiAndParams = (
  searchContext: SearchContext | SecretSearchContext,
  {
    query,
    maxCount,
    roleIds,
    fieldsQuery,
    includeInactive,
    shorten,
    from,
    to,
    normalize: normalizeParam,
    teamFilter,
  }: any,
) => {
  const baseSearchParams: BaseSearchRequestParams = { query, max: maxCount }
  const clientAndPatientsParams: SearchClientsAndPatientsRequestParams = {
    ...baseSearchParams,
    fieldsQuery,
    includeInactive,
  }
  const personsParams: SearchPersonsRequestParams = {
    ...baseSearchParams,
    from,
    includeInactive,
    teamFilter,
    roleIds,
    shorten,
    to,
  }
  const contactsParams: SearchContactsRequestParams = {
    ...baseSearchParams,
    from,
    to,
    normalize: normalizeParam,
  }

  const baseResponseHandler = ({
    data,
    totalCount,
  }: {
    data: unknown
    totalCount: number
  }) => ({ data, totalCount })

  const SearchContextMap = {
    [SearchContext.INVENTORY]: {
      api: API.searchInventory,
    },
    [SearchContext.PROCEDURES]: {
      api: API.searchProcedures,
    },
    [SearchContext.LAB_TESTS]: {
      api: API.searchLabTests,
    },
    [SearchContext.CLIENT_PATIENTS]: {
      params: clientAndPatientsParams,
      api: API.searchClientsAndPatients,
    },
    [SecretSearchContext.PERSONS]: {
      params: personsParams,
      api: API.searchPersons,
    },
    [SearchContext.REMINDER_PROTOCOLS]: {
      api: API.searchReminderProtocols,
    },
    [SearchContext.CONTACTS]: {
      params: contactsParams,
      api: API.searchContacts,
    },
    [SearchContext.TASK_DASHBOARD]: {
      api: API.searchTasks,
    },
    [SearchContext.MARKETPLACE]: {
      api: API.searchMarketplaceItems,
    },
    [SearchContext.IMAGING_DASHBOARD]: {
      api: API.suggestImagingDashboard,
    },
    [SearchContext.PRACTICES]: {
      api: API.suggestBusinesses,
    },
    [SearchContext.ON_HAND_CATALOG]: {
      api: API.suggestOnHandCatalog,
    },
    [SearchContext.INVENTORY_ORDERS]: {
      params: [from ?? 0, to ?? 25, undefined, undefined, query],
      api: API.fetchInventoryOrders,
      responseHandler: (
        response: Awaited<ReturnType<typeof API.fetchInventoryOrders>>,
      ) => ({
        data: response.result
          ? response.result.data.map(
              (id: string) => response.entities.inventoryOrders[id],
            )
          : response.data,
        totalCount: response.result?.totalCount,
      }),
    },
  }

  return {
    params: baseSearchParams,
    responseHandler: baseResponseHandler,
    ...SearchContextMap[searchContext],
  }
}

function* updatePersonsEntities(data: any) {
  const { entities } = normalize(data, Schema.userArray)
  yield call(updateEntities, entities)
}

function* updateContactEntities(data: any) {
  const { entities } = normalize(data, Schema.contactsArray)
  yield call(updateEntities, entities)
}

function* updateInventoryOrderEntities(data: any) {
  const { entities } = normalize(data, [Schema.inventoryOrder])
  yield call(updateEntities, entities)
}

const updateEntitiesHandlersMap: Partial<
  Record<SearchContext | SecretSearchContext, (data: any) => Generator>
> = {
  [SecretSearchContext.PERSONS]: updatePersonsEntities,
  [SearchContext.CLIENT_PATIENTS]: updatePersonsEntities,
  [SearchContext.CONTACTS]: updateContactEntities,
  [SearchContext.INVENTORY_ORDERS]: updateInventoryOrderEntities,
}

function* executeSearchRequest(
  searchApi: (...args: any[]) => any,
  searchParams: any,
  businessId?: string,
) {
  if (businessId) {
    return Array.isArray(searchParams)
      ? yield* requestAPIForBusiness<any, any>(
          businessId,
          searchApi,
          ...searchParams,
        )
      : yield* requestAPIForBusiness<typeof searchParams, any[]>(
          businessId,
          searchApi,
          searchParams,
        )
  }
  return Array.isArray(searchParams)
    ? yield* requestAPI<any, any>(searchApi, ...searchParams)
    : yield* requestAPI<typeof searchParams, any[]>(searchApi, searchParams)
}

export function* fetchSuggestionResultsSaga({
  businessId,
  searchContext,
  searchTerm: query,
  maxCount = Defaults.SEARCH_BATCH_LOAD_COUNT,
  roleIds,
  fieldsQuery,
  includeInactive = true,
  teamFilter = TeamFilter.MEMBER,
  shorten,
  stateId,
}: ReturnType<typeof fetchSuggestionResults>) {
  try {
    const {
      api: searchApi,
      params: searchParams,
      responseHandler,
    } = getSearchContextApiAndParams(searchContext, {
      query,
      maxCount,
      roleIds,
      fieldsQuery,
      includeInactive,
      teamFilter,
      shorten,
    })
    if (searchApi) {
      const response = yield* executeSearchRequest(
        searchApi,
        searchParams,
        businessId,
      )

      const { data, totalCount } = responseHandler(response)

      // TODO: adjust only map after removing isProceduresI18nEnabled
      const mapper = SearchResultToListItemMap[searchContext]
      const processedData = mapper ? data.map(mapper) : data
      const result = formatSuggestions(processedData)
      const updateEntityHandler = updateEntitiesHandlersMap[searchContext]
      if (updateEntityHandler) {
        yield updateEntityHandler(data)
      }
      yield put(fetchSuggestionResultsSuccess(result, totalCount, stateId))
    }
  } catch (error) {
    yield put(fetchSuggestionResultsFailure(error as ApiError))
  }
}

export function* fetchMoreSuggestionResultsCancelable({
  businessId,
  searchContext,
  searchTerm: query,
  maxCount = Defaults.SEARCH_BATCH_LOAD_COUNT,
  roleIds,
  fieldsQuery,
  includeInactive = true,
  teamFilter = TeamFilter.MEMBER,
  shorten,
  from = 0,
  to = 20,
}: ReturnType<typeof fetchMoreSuggestionResults>) {
  try {
    const {
      api: searchApi,
      params: searchParams,
      responseHandler,
    } = getSearchContextApiAndParams(searchContext, {
      query,
      maxCount,
      roleIds,
      fieldsQuery,
      includeInactive,
      teamFilter,
      shorten,
      from,
      to,
    })
    if (searchApi) {
      const response = yield* executeSearchRequest(
        searchApi,
        searchParams,
        businessId,
      )

      const { data, totalCount } = responseHandler(response)

      const mapper = SearchResultToListItemMap[searchContext]
      const processedData = mapper ? data.map(mapper) : data
      const result = formatSuggestions(processedData)
      const updateEntityHandler = updateEntitiesHandlersMap[searchContext]
      if (updateEntityHandler) {
        yield updateEntityHandler(data)
      }
      yield put(fetchMoreSuggestionResultsSuccess(result, totalCount, from))
    }
  } catch (error) {
    yield put(fetchMoreSuggestionResultsFailure(error as ApiError))
  }
}

export function* fetchMoreSuggestionResultsSaga(
  params: ReturnType<typeof fetchMoreSuggestionResults>,
) {
  const task: Task = yield fork(fetchMoreSuggestionResultsCancelable, params)
  yield take([
    FETCH_SUGGESTION_RESULTS,
    FETCH_SUGGESTION_RESULTS_SUCCESS,
    CLEAR_SUGGESTION_RESULTS,
  ])
  yield cancel(task)
}

function* watchFetchSuggestionResult() {
  yield takeLatest(FETCH_SUGGESTION_RESULTS, fetchSuggestionResultsSaga)
  yield takeLatest(
    FETCH_MORE_SUGGESTION_RESULTS,
    fetchMoreSuggestionResultsSaga,
  )
}

export default function* searchSaga() {
  yield all([watchFetchSuggestionResult()])
}
